import React from "react";

import { Person as PersonIcon, CalendarToday as CalendarIcon } from "@mui/icons-material";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import dayjs from "dayjs";

interface IExcludedChild {
  childId?: number;
  firstName?: string;
  lastName?: string;
  lastExcludedDate?: Date;
}

interface ExcludedChildCardProps {
  child: IExcludedChild;
}

export const ExcludedChildCard: React.FC<ExcludedChildCardProps> = ({ child }) => {
  const formatDate = (date: Date | undefined | null): string => {
    return date ? dayjs(date).format("DD-MMM-YYYY") : "-";
  };

  const fullName = [child.firstName, child.lastName].filter(Boolean).join(" ") || "N/A";

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "16px",
        border: "2px solid",
        borderColor: "grey.300",
        transition: "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
        "&:hover": {
          boxShadow: 4,
          borderColor: "primary.main",
        },
      }}
    >
      <CardHeader
        title={fullName}
        slotProps={{
          title: {
            variant: "subtitle1",
            fontWeight: 600,
            sx: { wordBreak: "break-word" },
          },
        }}
        subheader={
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Excluded Child
          </Typography>
        }
        action={
          <Chip
            icon={<CalendarIcon sx={{ fontSize: 14 }} />}
            label={formatDate(child.lastExcludedDate)}
            size="small"
            sx={{ backgroundColor: "#f5f5f5" }}
          />
        }
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ flex: 1, pt: 1 }}>
        <List dense disablePadding>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <PersonIcon sx={{ color: "text.secondary", fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="caption" color="text.secondary">
                  First Name
                </Typography>
              }
              secondary={<Typography variant="body2">{child.firstName || "-"}</Typography>}
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <PersonIcon sx={{ color: "text.secondary", fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="caption" color="text.secondary">
                  Last Name
                </Typography>
              }
              secondary={<Typography variant="body2">{child.lastName || "-"}</Typography>}
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CalendarIcon sx={{ color: "text.secondary", fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="caption" color="text.secondary">
                  Excluded On
                </Typography>
              }
              secondary={<Typography variant="body2">{formatDate(child.lastExcludedDate)}</Typography>}
            />
          </ListItem>
        </List>
      </CardContent>
    </Card>
  );
};
